/**
 * Cancelar a condicional: erro operacional, antes de qualquer decisão do
 * cliente — "abri por engano", "digitei o cliente errado". Motivo é
 * OBRIGATÓRIO (3 a 500 caracteres, mesmo `check` da migração 0024): o
 * diálogo recusa submeter sem ele, e o servidor confere de novo — a tela só
 * evita o 422 óbvio (CONSTITUICAO.md §4.1).
 */
import { useState, type FormEvent } from 'react'

import { Dialogo, RodapeDoDialogo } from '../../componentes/Dialogo'
import { MensagemDeErro } from '../../componentes/Campos'
import { ErroApi } from '../../plataforma/api'
import {
  cancelarCondicional,
  TAMANHO_MAXIMO_MOTIVO_CANCELAMENTO,
  TAMANHO_MINIMO_MOTIVO_CANCELAMENTO,
  type Condicional,
  type LinhaCondicional,
} from '../../plataforma/condicional'

export function DialogoCancelarCondicional({
  condicional,
  aoFechar,
  aoConcluir,
}: {
  condicional: LinhaCondicional
  aoFechar: () => void
  aoConcluir: (atualizada: Condicional) => void
}) {
  const [motivo, setMotivo] = useState('')
  const [tentou, setTentou] = useState(false)
  const [erro, setErro] = useState('')
  const [enviando, setEnviando] = useState(false)

  const motivoCurto = motivo.trim().length < TAMANHO_MINIMO_MOTIVO_CANCELAMENTO
  const erroMotivo =
    tentou && motivoCurto
      ? `Escreva pelo menos ${TAMANHO_MINIMO_MOTIVO_CANCELAMENTO} caracteres.`
      : ''

  async function enviar(evento: FormEvent) {
    evento.preventDefault()
    setTentou(true)
    if (motivoCurto) return

    setErro('')
    setEnviando(true)
    try {
      const atualizada = await cancelarCondicional(condicional.codigo, motivo)
      aoConcluir(atualizada)
    } catch (causa: unknown) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível cancelar a condicional.')
      setEnviando(false)
    }
  }

  return (
    <Dialogo
      icone="warning"
      titulo={`Cancelar ${condicional.codigo}?`}
      descricao={`A peça volta ao estoque, sem virar venda. Registre o motivo do cancelamento.`}
      aoFechar={aoFechar}
    >
      <form onSubmit={enviar} noValidate>
        <label htmlFor="motivo-cancelamento" className="mb-2 block text-[13px] font-semibold text-texto">
          Motivo do cancelamento
        </label>
        <textarea
          id="motivo-cancelamento"
          rows={3}
          maxLength={TAMANHO_MAXIMO_MOTIVO_CANCELAMENTO}
          value={motivo}
          onChange={(evento) => setMotivo(evento.target.value)}
          placeholder="Ex.: aberta por engano, cliente errado…"
          aria-invalid={erroMotivo ? true : undefined}
          className="campo w-full px-3 py-2 text-[13.5px]"
        />
        {erroMotivo && <MensagemDeErro>{erroMotivo}</MensagemDeErro>}

        {erro && <MensagemDeErro>{erro}</MensagemDeErro>}

        <RodapeDoDialogo
          confirmar="Cancelar condicional"
          confirmando="Cancelando…"
          ocupado={enviando}
          destrutivo
          aoCancelar={aoFechar}
        />
      </form>
    </Dialogo>
  )
}
